import {
  PORTAL_IMAGE_CONTRACT_VERSION,
  PORTAL_IMAGE_PRESETS,
  PORTAL_IMAGE_VIEWPORTS,
  getPortalImageAspectRatio,
  getPortalImageSlot,
  getPortalImageSurface,
} from "./portalImageContract";
import {
  extractCompatibleImageDelivery,
  extractCompatibleImageOptimizationMetadata,
  normalizeImageMigrationPayload,
} from "./imageMigrationCompatibility";
import { resolvePortalImageRollout } from "./portalImageRollout";
import { normalizePublicMediaUrl } from "./publicMediaUrl";
import {
  auditPortalDeliveryForSurface,
  validatePortalImageFallback,
} from "./imageContractIntegrity";
import {
  resolvePublicMediaContract,
  resolvePublicMediaCover,
  resolvePublicMediaPrimary,
} from "./publicMediaContract";

const STORAGE_BASE_URL = String(process.env.REACT_APP_STORAGE_BASE_URL || "").replace(/\/+$/, "");

const THUMB_MARKER_REGEX = /(?:-thumb-small|-thumb-mid|-thumb|thumbnail|\/thumb(?:[./_-]|$))/i;
const PORTAL_IMAGE_RESOLVER_CACHE_LIMIT = 500;
const portalImageResolverCache = new Map();
const ROLE_PREFERENCE = Object.freeze({
  mobile: ["mobile", "default", "tablet", "desktop", "global"],
  tablet: ["tablet", "desktop", "default", "mobile", "global"],
  desktop: ["desktop", "default", "tablet", "mobile", "global"],
});
const FORMAT_PREFERENCE = ["webp", "jpg", "jpeg", "png", "avif"];

export const resolveStorageUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return normalizePublicMediaUrl(raw);
  if (raw.startsWith("/")) {
    return STORAGE_BASE_URL ? normalizePublicMediaUrl(`${STORAGE_BASE_URL}${raw}`) : raw;
  }
  return STORAGE_BASE_URL ? normalizePublicMediaUrl(`${STORAGE_BASE_URL}/${raw.replace(/^\/+/, "")}`) : raw;
};

const pickFromObject = (input, keys = []) => {
  if (!input || typeof input !== "object") return "";
  for (const key of keys) {
    const value = input?.[key];
    if (!value) continue;
    if (typeof value === "string") {
      const text = resolveStorageUrl(value);
      if (text) return text;
      continue;
    }
    if (typeof value === "object") {
      const nested = resolveImageUrl(value);
      if (nested) return nested;
    }
  }
  return "";
};

const getPathValue = (input, path = "") => {
  if (!input || !path) return undefined;
  return String(path)
    .split(".")
    .filter(Boolean)
    .reduce((current, segment) => {
      if (current === undefined || current === null) return undefined;
      if (current instanceof Map) return current.get(segment);
      return current[segment];
    }, input);
};

const findFirstRenderableValue = (input, paths = []) => {
  for (const path of paths) {
    const candidate = getPathValue(input, path);
    if (resolveImageUrl(candidate, "")) return candidate;
  }
  return null;
};

const normalizeViewport = (viewport = "desktop") =>
  PORTAL_IMAGE_VIEWPORTS.includes(viewport) ? viewport : "desktop";

const roleRank = (role = "", viewport = "desktop") => {
  const preference = ROLE_PREFERENCE[normalizeViewport(viewport)] || ROLE_PREFERENCE.desktop;
  const index = preference.indexOf(String(role || "default"));
  return index === -1 ? preference.length : index;
};

const formatRank = (format = "") => {
  const index = FORMAT_PREFERENCE.indexOf(String(format || "").toLowerCase());
  return index === -1 ? FORMAT_PREFERENCE.length : index;
};

const joinDeliveryUrl = (baseUrl = "", path = "") => {
  const rawPath = String(path || "").trim();
  if (!rawPath) return "";
  if (/^https?:\/\//i.test(rawPath) || rawPath.startsWith("data:")) return normalizePublicMediaUrl(rawPath);
  const base = String(baseUrl || "").trim().replace(/\/+$/, "");
  const normalizedPath = `/${rawPath.replace(/^\/+/, "")}`;
  return resolveStorageUrl(base ? `${base}${normalizedPath}` : normalizedPath);
};

const cachePortalImagePlan = (key, plan) => {
  if (!key) return plan;
  if (portalImageResolverCache.size >= PORTAL_IMAGE_RESOLVER_CACHE_LIMIT) {
    const oldestKey = portalImageResolverCache.keys().next().value;
    portalImageResolverCache.delete(oldestKey);
  }
  portalImageResolverCache.set(key, plan);
  return plan;
};

export const clearPortalImageResolverCache = () => portalImageResolverCache.clear();

export const getPortalImageResolverCacheSize = () => portalImageResolverCache.size;

export const isThumbnailUrl = (value = "") => THUMB_MARKER_REGEX.test(String(value || "").trim());

// Retained as a compatibility export. Phase 6 forbids guessing an object that
// is not present in an authoritative delivery descriptor.
export const buildThumbnailVariantUrl = () => "";

export const resolveImageUrl = (value, fallback = "") => {
  fallback = normalizePublicMediaUrl(fallback);
  if (!value) return fallback;
  if (typeof value === "string") return resolveStorageUrl(value) || fallback;
  if (Array.isArray(value)) {
    for (const item of value) {
      const resolved = resolveImageUrl(item);
      if (resolved) return resolved;
    }
    return fallback;
  }
  if (typeof value !== "object") return fallback;

  // The public media envelope is authoritative for entity consumers. In
  // particular, an explicit empty contract must not revive stale root fields.
  const publicMedia = resolvePublicMediaContract(value);
  if (publicMedia) {
    const publishedMedia = resolvePublicMediaCover(value) || resolvePublicMediaPrimary(value);
    return publishedMedia ? resolveImageUrl(publishedMedia, fallback) : fallback;
  }

  const resolved = pickFromObject(value, [
    "url",
    "variantUrl",
    "src",
    "image",
    "heroImage",
    "coverImage",
    "bestUrl",
    "originalUrl",
    "assetUrl",
    "key",
    "thumbnailUrl",
    "thumbnail",
    "thumb",
  ]);

  return resolved || fallback;
};

export const extractImageOptimizationMetadata = (value) => {
  return extractCompatibleImageOptimizationMetadata(value);
};

export const extractImageDelivery = (value) => {
  return extractCompatibleImageDelivery(value);
};

export const parseOptimizedImageUrl = (value) => {
  const url = resolveStorageUrl(value);
  if (!url) return null;
  let origin = "";
  let pathname = url;
  try {
    const parsed = new URL(url);
    origin = parsed.origin;
    pathname = parsed.pathname;
  } catch (_error) {
    pathname = url.split(/[?#]/)[0];
  }
  const match = pathname.match(/^(.*)\/media\/([^/]+)\/v([^/]+)\/([^/]+)\/([^/.]+)\.([a-z0-9]+)$/i);
  const helperMatch = !match
    ? pathname.match(/^(.*)\/media\/([^/]+)\/v([^/]+)\/(thumb|blur)\.([a-z0-9]+)$/i)
    : null;
  if (!match && !helperMatch) return null;
  if (helperMatch) {
    return Object.freeze({
      url,
      baseUrl: `${origin}${helperMatch[1]}`.replace(/\/+$/, ""),
      imageCode: helperMatch[2],
      version: helperMatch[3],
      role: "global",
      variantKey: helperMatch[4].toLowerCase(),
      format: helperMatch[5].toLowerCase(),
    });
  }
  return Object.freeze({
    url,
    baseUrl: `${origin}${match[1]}`.replace(/\/+$/, ""),
    imageCode: match[2],
    version: match[3],
    role: match[4],
    variantKey: match[5],
    format: match[6].toLowerCase(),
  });
};

const resolveDescriptorVariant = ({ delivery, surfaceKey, slotKey, viewport }) => {
  const audit = auditPortalDeliveryForSurface(delivery, { surfaceKey, slotKey });
  const matches = [...audit.valid]
    .sort((left, right) =>
      roleRank(left.role, viewport) - roleRank(right.role, viewport) ||
      formatRank(left.format) - formatRank(right.format));
  return {
    selected: matches[0] || null,
    candidates: matches,
    rejected: audit.rejected,
    integrityStatus: audit.status,
  };
};

const resolveSurfaceInput = (value, surfaceDefinition) => {
  const override = findFirstRenderableValue(value, surfaceDefinition?.overrideFields || []);
  if (override) return override;
  if (
    surfaceDefinition?.presetKey === PORTAL_IMAGE_PRESETS.ENTITY
    && ["card", "thumbnail"].includes(surfaceDefinition?.kind)
  ) {
    return findFirstRenderableValue(value, [
      "publicMedia.cover",
      "publicMedia.primary",
      "publicGallery.cover",
      "canonicalImageDelivery.gallery.0",
      "coverImage",
      "destinationMedia.featured.main",
      "inventoryMedia.featured.main",
      "experienceMedia.featured.main",
    ]) || value;
  }
  return value;
};

const resolveSurfaceLegacyUrl = (value, surfaceDefinition, fallback = "") => {
  const retainedLegacyCandidate = findFirstRenderableValue(value, [
    "legacyUrl",
    "legacyFallbackUrl",
    "fallbackUrl",
    "imageOptimization.delivery.legacyUrl",
    "metadata.imageOptimization.delivery.legacyUrl",
  ]);
  const retainedLegacyUrl = resolveImageUrl(retainedLegacyCandidate, "");
  if (retainedLegacyUrl) return retainedLegacyUrl;
  if (surfaceDefinition?.constrainLegacyToThumbnail) {
    const candidate = findFirstRenderableValue(value, [
      "heroMedia.slots.thumbnail",
      "thumbnailUrl",
      "thumbnailImage",
      "thumbnail",
      "thumb",
      "previewUrl",
      "preview",
    ]);
    const candidateUrl = resolveImageUrl(candidate, "");
    if (candidateUrl) return candidateUrl;
    const directUrl = resolveImageUrl(value, "");
    if (isThumbnailUrl(directUrl)) return directUrl;
    const fallbackUrl = resolveStorageUrl(fallback);
    return isThumbnailUrl(fallbackUrl) ? fallbackUrl : "";
  }
  const candidate = findFirstRenderableValue(value, surfaceDefinition?.legacyFields || []);
  return resolveImageUrl(candidate || value, fallback) || fallback;
};

const resolveSurfaceRollbackUrl = (value, surfaceDefinition, fallback = "") => {
  for (const path of surfaceDefinition?.legacyFields || []) {
    const candidate = getPathValue(value, path);
    const url = resolveImageUrl(candidate, "");
    if (!url) continue;
    const compatibility = normalizeImageMigrationPayload(candidate);
    const isMaterializedUrl = /\/media\/[^/]+\/v[^/]+\/[^/]+\/[^/.]+\.[a-z0-9]+(?:[?#]|$)/i.test(url);
    if (!compatibility.metadata && !isMaterializedUrl) return url;
  }
  return resolveSurfaceLegacyUrl(value, surfaceDefinition, fallback);
};

const buildResolverCacheKey = ({ surfaceKey, viewport, slotKey, legacyUrl, fallback, delivery, identity, rollout }) => {
  const variants = Array.isArray(delivery?.variants) ? delivery.variants : [];
  const deliveryKey = delivery
    ? [
        delivery.imageCode,
        delivery.version,
        delivery.presetKey,
        delivery.baseUrl,
        variants.map((variant) => `${variant.slotKey}:${variant.role}:${variant.format}:${variant.path || variant.url}`).join("|"),
      ].join(";")
    : identity
      ? [identity.imageCode, identity.version, identity.presetKey, identity.baseUrl].join(";")
      : "legacy";
  const rolloutKey = rollout
    ? [
        rollout.stage,
        rollout.percentage,
        rollout.optimizedEnabled,
        rollout.reason,
        rollout.bucket,
        rollout.legacyFallbackAllowed,
        rollout.fallbackDecisionReason,
        rollout.fallbackRemovalBucket,
      ].join(":")
    : "rollout-default";
  return [surfaceKey, viewport, slotKey, legacyUrl, fallback, deliveryKey, rolloutKey].join("::");
};

export const resolvePortalImage = (
  value,
  { surfaceKey = "", viewport = "desktop", fallback = "", rolloutConfig = null } = {},
) => {
  const normalizedViewport = normalizeViewport(viewport);
  const surfaceDefinition = getPortalImageSurface(surfaceKey);
  const slotKey = surfaceDefinition ? getPortalImageSlot(surfaceKey, normalizedViewport) : "";
  const publicMedia = resolvePublicMediaContract(value);
  const authoritativeEmpty = publicMedia?.state === "empty";
  const effectiveFallback = authoritativeEmpty ? "" : normalizePublicMediaUrl(fallback);
  const resolvedInput = authoritativeEmpty
    ? null
    : surfaceDefinition
      ? resolveSurfaceInput(value, surfaceDefinition)
      : value;
  const projectedLegacyUrl = authoritativeEmpty
    ? ""
    : resolveSurfaceLegacyUrl(resolvedInput, surfaceDefinition, effectiveFallback);
  const resolvedCompatibility = normalizeImageMigrationPayload(resolvedInput);
  // Once the backend has explicitly published an empty contract, root-level
  // optimization metadata belongs to retired media just as much as the old
  // hero/gallery URLs do. Do not let it re-enter through compatibility mode.
  const rootCompatibility = authoritativeEmpty
    ? normalizeImageMigrationPayload(null)
    : resolvedInput === value
      ? resolvedCompatibility
      : normalizeImageMigrationPayload(value);
  const resolvedInputHasCanonicalIdentity = Boolean(
    resolvedCompatibility.delivery || resolvedCompatibility.identity,
  );
  const compatibility = resolvedInput !== value && resolvedInputHasCanonicalIdentity
    ? resolvedCompatibility
    : rootCompatibility.delivery || rootCompatibility.identity
      ? rootCompatibility
      : resolvedCompatibility;
  const metadata = compatibility.metadata;
  const delivery = compatibility.delivery;
  const identity = compatibility.identity;
  const descriptorFallback = resolveStorageUrl(delivery?.fallbackUrl || "");
  const deliveryThumbnail = resolveStorageUrl(delivery?.thumbnailUrl || metadata?.thumbnailUrl || "");
  const rollout = resolvePortalImageRollout({
    key: identity?.imageCode || delivery?.imageCode || projectedLegacyUrl || effectiveFallback || surfaceKey,
    surfaceKey,
    config: rolloutConfig,
  });
  const projectedMaterialized = !delivery ? parseOptimizedImageUrl(projectedLegacyUrl) : null;
  const projectedMaterializedUrl =
    surfaceDefinition?.kind === "thumbnail" && projectedMaterialized?.variantKey === "thumb"
      ? projectedLegacyUrl
      : "";
  const projectedLegacyCandidate = rollout.optimizedEnabled
    ? projectedMaterializedUrl || (rollout.legacyFallbackAllowed ? projectedLegacyUrl : "")
    : resolveSurfaceRollbackUrl(resolvedInput, surfaceDefinition, effectiveFallback);
  const constrainedFallback = surfaceDefinition?.constrainLegacyToThumbnail;
  const legacyValidation = validatePortalImageFallback(projectedLegacyCandidate, {
    slotKey,
    // resolveSurfaceLegacyUrl has already enforced thumbnail-only surfaces.
    allowLegacy: true,
    allowMaterializedAsDeclared: !delivery || constrainedFallback || !rollout.optimizedEnabled,
  });
  const legacyUrl = legacyValidation.valid ? projectedLegacyCandidate : "";
  const descriptorFallbackValidation = validatePortalImageFallback(descriptorFallback, {
    slotKey,
    allowLegacy: false,
  });
  const safeDescriptorFallback = descriptorFallbackValidation.valid ? descriptorFallback : "";
  const explicitFallback = rollout.legacyFallbackAllowed ? resolveStorageUrl(effectiveFallback) : "";
  const explicitFallbackValidation = validatePortalImageFallback(explicitFallback, {
    slotKey,
    allowLegacy: !constrainedFallback,
    allowMaterializedAsDeclared: !delivery || constrainedFallback || !rollout.optimizedEnabled,
  });
  const safeExplicitFallback = explicitFallbackValidation.valid ? explicitFallback : "";
  const fallbackUrl = legacyUrl || (constrainedFallback
    ? deliveryThumbnail
    : safeDescriptorFallback || safeExplicitFallback);
  const cacheKey = buildResolverCacheKey({
    surfaceKey,
    viewport: normalizedViewport,
    slotKey,
    legacyUrl,
    fallback: effectiveFallback,
    delivery,
    identity,
    rollout,
  });
  const cached = portalImageResolverCache.get(cacheKey);
  if (cached) return cached;

  let selected = null;
  let candidates = [];
  let rejectedCandidates = [];
  let integrityStatus = delivery && slotKey ? "missing-variant" : "legacy";
  let source = authoritativeEmpty
    ? "authoritative-empty"
    : rollout.optimizedEnabled
      ? rollout.legacyFallbackAllowed ? "legacy" : "fallback-removed"
      : "rollout-legacy";
  const useCanonicalGalleryThumbnail = Boolean(
    surfaceDefinition?.kind === "thumbnail"
    && delivery?.presetKey === PORTAL_IMAGE_PRESETS.GALLERY_MEDIA,
  );
  if (
    rollout.optimizedEnabled &&
    delivery &&
    slotKey &&
    (surfaceDefinition?.preferDeliveryThumbnail || useCanonicalGalleryThumbnail) &&
    deliveryThumbnail
  ) {
    selected = {
      slotKey,
      role: "global",
      variantKey: "thumb",
      format: "webp",
      url: deliveryThumbnail,
      width: useCanonicalGalleryThumbnail ? 300 : 320,
      height: useCanonicalGalleryThumbnail ? 300 : 220,
    };
    candidates = [selected];
    source = "delivery-thumbnail";
    integrityStatus = "valid";
  } else if (rollout.optimizedEnabled && delivery && slotKey) {
    const descriptorMatch = resolveDescriptorVariant({
      delivery,
      surfaceKey,
      slotKey,
      viewport: normalizedViewport,
    });
    selected = descriptorMatch.selected;
    candidates = descriptorMatch.candidates;
    rejectedCandidates = descriptorMatch.rejected;
    integrityStatus = descriptorMatch.integrityStatus;
    source = selected ? "delivery" : "delivery-fallback";
  }

  const parsedFallback = parseOptimizedImageUrl(fallbackUrl || descriptorFallback);
  const supportsCanonicalGalleryCover = ["card", "thumbnail"].includes(
    surfaceDefinition?.kind,
  );
  const canonicalCoverUrl =
    rollout.optimizedEnabled &&
    supportsCanonicalGalleryCover &&
    delivery?.presetKey === PORTAL_IMAGE_PRESETS.GALLERY_MEDIA
      ? surfaceDefinition?.kind === "thumbnail"
        ? deliveryThumbnail || resolveImageUrl(resolvedInput, "")
        : resolveImageUrl(resolvedInput, "")
      : "";
  const safeCanonicalCoverUrl = parseOptimizedImageUrl(canonicalCoverUrl)
    ? canonicalCoverUrl
    : "";
  if (rollout.optimizedEnabled && !delivery && parsedFallback) {
    // A materialized URL is safe to use as-is. Never predict sibling slot,
    // blur, or thumbnail paths: only the delivery descriptor can declare them.
    source = "materialized";
  } else if (rollout.optimizedEnabled && !delivery && identity?.imageCode) {
    source = "partial-fallback";
  }

  const selectedUrl =
    selected?.url ||
    safeCanonicalCoverUrl ||
    (rollout.optimizedEnabled ? safeDescriptorFallback : "") ||
    fallbackUrl;
  const selectedParsed = parseOptimizedImageUrl(selectedUrl) || parsedFallback;
  const blurUrl = rollout.optimizedEnabled
    ? resolveStorageUrl(delivery?.blurUrl || metadata?.blurUrl || "")
    : "";
  const thumbnailUrl = rollout.optimizedEnabled
    ? resolveStorageUrl(delivery?.thumbnailUrl || metadata?.thumbnailUrl || "")
    : "";
  const fallbackUrls = Array.from(new Set([
    !constrainedFallback && rollout.optimizedEnabled ? safeDescriptorFallback : "",
    rollout.legacyFallbackAllowed ? legacyUrl : "",
    !constrainedFallback && rollout.legacyFallbackAllowed ? resolveStorageUrl(effectiveFallback) : "",
  ].filter(Boolean))).filter((url) => url !== selectedUrl);
  const migrationState = source === "authoritative-empty"
    ? "empty"
    : source === "rollout-legacy"
    ? "rollout-legacy"
    : source === "delivery"
      ? "projected"
      : source === "delivery-thumbnail"
        ? "projected"
      : source === "delivery-fallback"
        ? "projected-fallback"
        : source === "partial-fallback"
          ? "partial"
          : source === "materialized"
            ? "materialized"
            : "legacy";
  const plan = Object.freeze({
    contractVersion: PORTAL_IMAGE_CONTRACT_VERSION,
    surfaceKey,
    viewport: normalizedViewport,
    slotKey,
    presetKey: surfaceDefinition?.presetKey || delivery?.presetKey || "",
    source,
    migrationState,
    migrationSourcePath: compatibility.sourcePath,
    rollout,
    legacyFallbackAllowed: rollout.legacyFallbackAllowed,
    optimized: Boolean(rollout.optimizedEnabled && (selected || selectedParsed)),
    url: selectedUrl,
    fallbackUrl,
    fallbackUrls: Object.freeze(fallbackUrls),
    blurUrl,
    thumbnailUrl,
    role: selected?.role || metadata?.role || selectedParsed?.role || "",
    variantKey: selected?.variantKey || metadata?.variantKey || selectedParsed?.variantKey || "",
    format: selected?.format || metadata?.format || selectedParsed?.format || "",
    width: selected?.width || Number(metadata?.width || 0) || null,
    height: selected?.height || Number(metadata?.height || 0) || null,
    aspectRatio: surfaceDefinition ? getPortalImageAspectRatio(surfaceKey, normalizedViewport) : null,
    fit: surfaceDefinition?.fit || "cover",
    priority: surfaceDefinition?.priority || "standard",
    sizePolicy: surfaceDefinition?.sizePolicy || null,
    integrityStatus,
    missingVariant: integrityStatus === "missing-variant",
    rejectedCandidates: Object.freeze(rejectedCandidates.map((entry) => Object.freeze({
      reasons: Object.freeze([...(entry.reasons || [])]),
      slotKey: entry.candidate?.slotKey || "",
      role: entry.candidate?.role || "",
      variantKey: entry.candidate?.variantKey || "",
      format: entry.candidate?.format || "",
    }))),
    candidates: Object.freeze(candidates.map((candidate) => Object.freeze({ ...candidate }))),
  });
  return cachePortalImagePlan(cacheKey, plan);
};

export const resolveThumbnailUrl = (
  value,
  size = "mid",
  fallbackToImage = true,
  options = {},
) => {
  if (!value) return "";

  const full = resolveImageUrl(value);

  if (typeof value === "string") {
    if (isThumbnailUrl(full)) return full;
    return fallbackToImage ? full : "";
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const resolved = resolveThumbnailUrl(item, size, false, options);
      if (resolved) return resolved;
    }
    if (fallbackToImage) {
      const image = resolveImageUrl(value);
      return image ? resolveThumbnailUrl(image, size, true, options) : "";
    }
    return "";
  }

  if (typeof value !== "object") return fallbackToImage ? full : "";

  const publicMedia = resolvePublicMediaContract(value);
  if (publicMedia) {
    const publishedMedia = resolvePublicMediaCover(value) || resolvePublicMediaPrimary(value);
    return publishedMedia
      ? resolveThumbnailUrl(publishedMedia, size, fallbackToImage, options)
      : "";
  }

  const delivery = extractImageDelivery(value);
  const deliveryThumbnail = resolveStorageUrl(delivery?.thumbnailUrl || "");
  if (deliveryThumbnail) return deliveryThumbnail;
  const deliveryThumbnailVariant = (Array.isArray(delivery?.variants) ? delivery.variants : [])
    .filter((variant) => {
      const marker = `${variant?.slotKey || ""} ${variant?.variantKey || variant?.variant || ""}`;
      const width = Number(variant?.width || 0);
      return /thumb|preview|search_result/i.test(marker) && (!width || width <= 640);
    })
    .sort((left, right) => Number(right?.width || 0) - Number(left?.width || 0))[0];
  const deliveryThumbnailVariantUrl = deliveryThumbnailVariant
    ? joinDeliveryUrl(delivery?.baseUrl, deliveryThumbnailVariant.url || deliveryThumbnailVariant.path)
    : "";
  if (deliveryThumbnailVariantUrl) return deliveryThumbnailVariantUrl;

  const small = pickFromObject(value, [
    "thumbnailSmallUrl",
    "thumbnailSmall",
    "thumbSmallUrl",
    "thumbSmall",
  ]);
  const mid = pickFromObject(value, [
    "thumbnailMidUrl",
    "thumbnailMid",
    "thumbnailUrl",
    "thumbnailImage",
    "thumbnail",
    "thumb",
    "previewUrl",
    "preview",
  ]);
  const large = pickFromObject(value, ["thumbnailUrl", "thumbnail", "thumb"]);

  const explicitBySize =
    (size === "small" ? small : "") ||
    (size === "mid" ? mid : "") ||
    (size === "large" ? large : "") ||
    mid ||
    small ||
    large;

  if (explicitBySize) return explicitBySize;
  if (isThumbnailUrl(full)) return full;
  return fallbackToImage ? full : "";
};

export const toImageSet = (value, size = "mid") => {
  const full = resolveImageUrl(value);
  if (!full) return null;
  const thumb = resolveThumbnailUrl(value, size, true) || full;
  return { full, thumb };
};

export const resolveThumbnailBySize = (value, size = "mid", fallback = "") =>
  resolveThumbnailUrl(value, size, true) || normalizePublicMediaUrl(fallback);

export const resolveRenderableImage = (value, fallback = "", options = {}) =>
  options?.surfaceKey
    ? resolvePortalImage(value, { ...options, fallback }).url
    : resolveThumbnailUrl(value, "mid", true) || resolveImageUrl(value, fallback) || normalizePublicMediaUrl(fallback);

const imageVariantUtils = {
  isThumbnailUrl,
  buildThumbnailVariantUrl,
  resolveImageUrl,
  resolvePortalImage,
  resolveThumbnailUrl,
  resolveThumbnailBySize,
  resolveRenderableImage,
  resolveStorageUrl,
  parseOptimizedImageUrl,
  extractImageDelivery,
  extractImageOptimizationMetadata,
  clearPortalImageResolverCache,
  getPortalImageResolverCacheSize,
  toImageSet,
};

export default imageVariantUtils;
